import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
  Description,
} from '@mui/icons-material';
import { DocumentTrackingData, DayDocumentStatus } from '../../types/widget';
import { apiService } from '../../services/api';

const DocumentStatusWidget: React.FC = () => {
  const [data, setData] = useState<DocumentTrackingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getDocumentTrackingData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch document tracking data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'received':
        return <CheckCircle sx={{ color: '#48BB78', fontSize: 18 }} />;
      case 'overdue':
      case 'missing':
        return <Cancel sx={{ color: '#F56565', fontSize: 18 }} />;
      case 'due':
        return <Schedule sx={{ color: '#ECC94B', fontSize: 18 }} />;
      case 'pending':
        return <Schedule sx={{ color: '#A0AEC0', fontSize: 18 }} />;
      default:
        return <Schedule sx={{ color: '#A0AEC0', fontSize: 18 }} />;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'received':
        return '✓';
      case 'overdue':
        return '✗';
      case 'missing':
        return '✗';
      case 'due':
        return '⏳';
      case 'pending':
        return '—';
      default:
        return '—';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No document tracking data available.</Alert>;
  }

  const overdueCount = data.summary?.overdueCount || 0;

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1A202C',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #4A5568',
          backgroundColor: '#2D3748',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: '#A0AEC0' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Document Status
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchData} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {overdueCount > 0 ? (
            <Chip
              icon={<Warning sx={{ color: '#F56565 !important' }} />}
              label={`${overdueCount} Overdue`}
              size="small"
              sx={{
                backgroundColor: 'rgba(245, 101, 101, 0.2)',
                color: '#F56565',
                fontWeight: 'bold',
              }}
            />
          ) : (
            <Chip
              icon={<CheckCircle sx={{ color: '#48BB78 !important' }} />}
              label="All Current"
              size="small"
              sx={{
                backgroundColor: 'rgba(72, 187, 120, 0.2)',
                color: '#48BB78',
                fontWeight: 'bold',
              }}
            />
          )}
          <Chip
            label={`${data.summary?.completenessPercent || 0}% Complete`}
            size="small"
            sx={{ backgroundColor: '#4A5568' }}
          />
        </Box>
      </Box>

      {/* Document Status Table */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: '#4A5568', py: 0.75 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#A0AEC0' }}>Day</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#A0AEC0' }}>
                CS
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#A0AEC0' }}>
                DPR
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#A0AEC0' }}>
                Hot Cost
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.daysStatus.map((day: DayDocumentStatus) => {
              if (!day || !day.callSheet || !day.dpr || !day.hotCost) return null;
              const isCurrentDay = day.dayNumber === data.currentDay;
              return (
                <TableRow
                  key={day.dayNumber}
                  sx={{
                    ...(isCurrentDay && {
                      backgroundColor: 'rgba(246, 173, 85, 0.1)',
                    }),
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: isCurrentDay ? 'bold' : 'normal' }}>
                        {day.dayNumber}
                      </Typography>
                      {isCurrentDay && (
                        <Chip
                          label="TODAY"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.6rem',
                            backgroundColor: '#F6AD55',
                            color: '#1A202C',
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`Call Sheet: ${day.callSheet?.status || 'pending'}`}>
                      {getStatusIcon(day.callSheet?.status || 'pending')}
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`DPR: ${day.dpr?.status || 'pending'}`}>
                      {getStatusIcon(day.dpr?.status || 'pending')}
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`Hot Cost: ${day.hotCost?.status || 'pending'}`}>
                      {getStatusIcon(day.hotCost?.status || 'pending')}
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          p: 1,
          borderTop: '1px solid #4A5568',
          backgroundColor: '#2D3748',
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircle sx={{ color: '#48BB78', fontSize: 14 }} />
          <Typography variant="caption" color="text.secondary">
            Received
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Cancel sx={{ color: '#F56565', fontSize: 14 }} />
          <Typography variant="caption" color="text.secondary">
            Missing
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Schedule sx={{ color: '#ECC94B', fontSize: 14 }} />
          <Typography variant="caption" color="text.secondary">
            Due
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default DocumentStatusWidget;
